import {DiagramType, DiagramWidgetData} from '@dv5/domain'
import React, {CSSProperties, FC, useEffect, useRef, useState} from 'react'
import {useDiagramWidgetUpdate} from "./useDiagramWidgetUpdate"
import {diagramExamples, type DiagramExample} from "./diagramExamples"
import './DiagramWidgetToolbar.less'

interface Props {
    canvasId: string
    widgetId: string
    data: DiagramWidgetData
    toolbarStyles: CSSProperties
    onExpandToFull?: () => void
}

export const DiagramWidgetToolbar: FC<Props> = ({
                                                    canvasId,
                                                    widgetId,
                                                    data,
                                                    toolbarStyles,
                                                    onExpandToFull,
                                                }) => {
    const {updateData} = useDiagramWidgetUpdate({canvasId, widgetId})
    const [openDropdown, setOpenDropdown] = useState<DiagramType | null>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setOpenDropdown(null)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleTypeChange = (newType: DiagramType) => {
        updateData(['type'], {...data, diagramType: newType})
    }

    const handleExampleSelect = (example: DiagramExample) => {
        updateData(['text', 'diagramType'], {...data, text: example.sourceCode, diagramType: example.diagramType})
        setOpenDropdown(null)
    }

    const toggleDropdown = (diagramType: DiagramType) => {
        setOpenDropdown(openDropdown === diagramType ? null : diagramType)
    }

    const examplesForType = (type: DiagramType) => diagramExamples.filter(e => e.diagramType === type)

    return (
        <div className='diagram-widget-toolbar' style={toolbarStyles} ref={dropdownRef}>
            <div className='diagram-widget-toolbar__type-selector'>
                {Object.values(DiagramType).map((diagramType) => {
                    const examples = examplesForType(diagramType)
                    return (
                        <div key={diagramType} className='diagram-widget-toolbar__type-wrapper'>
                            <button
                                className={`diagram-widget-toolbar__button ${data.diagramType === diagramType ? 'diagram-widget-toolbar__button--active' : ''}`}
                                onMouseDown={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    handleTypeChange(diagramType)
                                }}
                                aria-label={`Select ${diagramType} type`}
                                type='button'
                            >
                                {diagramType}
                            </button>
                            {examples.length > 0 && (
                                <button
                                    className='diagram-widget-toolbar__dropdown-toggle'
                                    onMouseDown={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        toggleDropdown(diagramType)
                                    }}
                                    aria-label={`${diagramType} examples`}
                                    type='button'
                                >
                                    ▾
                                </button>
                            )}
                            {openDropdown === diagramType && examples.length > 0 && (
                                <div className='diagram-widget-toolbar__dropdown'>
                                    {examples.map(example => (
                                        <button
                                            key={example.name}
                                            className='diagram-widget-toolbar__dropdown-item'
                                            onMouseDown={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                handleExampleSelect(example)
                                            }}
                                            type='button'
                                        >
                                            {example.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
            {onExpandToFull && (
                <>
                    <div className='diagram-widget-toolbar__divider'/>
                    <button
                        className='diagram-widget-toolbar__button diagram-widget-toolbar__button--expand'
                        onMouseDown={onExpandToFull}
                        aria-label='Expand to full editor'
                        type='button'
                    >
                        ↗
                    </button>
                </>
            )}
        </div>
    )
}
